import React, { useState, useEffect } from "react";
import { Form, Select, Typography } from "antd";
import { useTranslation } from "react-i18next";
import EmptyDamageClassForm from "../Surveys/EmptyDamageClassForm";
import PeopleDamageForm from "../Surveys/PeopleDamageForm";
import FunctionalityDamageForm from "./FunctionalityDamageForm";
import StructuralDamageForm from "./StructuralDamageForm";
import NotStructuralDamageForm from "./NotStructuralDamageForm";
import DamageChurchForm from "./DamageChurchForm";
import axios from "axios";

const { Option } = Select;
const { Title } = Typography;

const BuildingComponentDamageForm = props => {
  // Hooks must run unconditionally on every render — keep them at the top
  // even though we may short-circuit to DamageChurchForm below.
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [damageClass, setDamageClass] = useState();
  const [damageClasses, setDamageClasses] = useState([]);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/damage-classes")
      .then(({ data }) => {
        setDamageClasses(data);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (props.record.damage) {
      form.setFieldsValue({
        damageClass: props.record.damage.damageClass,
      });
      setDamageClass(props.record.damage.damageClass);
    }
  }, [props.record.damage, form]);

  // When the parent is a non-ordinary church, building damage is recorded
  // per Italian Civil Protection mechanism (M01–M28) instead of the
  // ordinary AeDES damage classes. Route to DamageChurchForm — but be
  // careful not to swallow legacy DamageBuildingStructural / NotStructural /
  // Functionality damages: their typeId/damage_class fields would silently
  // disappear when re-saved through the church form (MapStruct merges into
  // the existing entity and drops unknown fields). So only use the church
  // form when:
  //   (a) the loaded damage already has a CHURCH/WHOLE_STRUCTURE discriminator
  //       (editing a previously-saved church damage), OR
  //   (b) we're creating a new damage on a church parent (no existing entity
  //       to preserve), identified by id missing or sentinel -1.
  const damage = props.record?.damage;
  const isChurchParent = props.parentEnterprise?.subtype === "CULTURAL_HERITAGE_CHURCH";
  const existingDiscriminator = damage?.damageDiscriminator;
  const isChurchDamage = existingDiscriminator === "CHURCH_STRUCTURE"
                      || existingDiscriminator === "WHOLE_STRUCTURE";
  const isNewDamage = !damage?.id || damage.id === -1;
  if (isChurchDamage || (isChurchParent && isNewDamage)) {
    return (
      <DamageChurchForm
        record={props.record}
        parentCallback={props.parentCallback}
        isEditMode={props.isEditMode}
      />
    );
  }

  const components = {
    'empty': EmptyDamageClassForm,
    'structural': StructuralDamageForm,
    'notstructural': NotStructuralDamageForm,
    'people': PeopleDamageForm,
    'functionality': FunctionalityDamageForm,
  };

  const handleChange = (value) => {
    setDamageClass(value);
    const newDamage = { ...props.record };
    newDamage.damage.damageClass = value;
    // No required field of its own; don't leak the parent's validity (the sub-form reports its own).
    newDamage.valid = true;
    props.parentCallback(newDamage);
  };

  const getComponents = () => {
    if (damageClass) {
      const name = damageClass.toLowerCase();
      const DetailsComponent = components[name] || EmptyDamageClassForm;
      return <DetailsComponent record={props.record} parentCallback={props.parentCallback} damageClass={props.record.damage.damageClass || damageClass} />
    }
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="details_form"
      name="buildingDamageForm"
    >
      <Title className="details_form_header"
        title="Damages details"
        subTitle="Building"
      />
      <Form.Item
        label="Damage class"
        name="damageClass"
        tooltip="Damage class"
      >
        <Select onChange={handleChange}>
          {damageClasses.map(name => (
            <Option key={name} value={name}>
              {t('damageEnums.damageClass.' + name, name)}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {getComponents()}
    </Form>
  );
};

export default BuildingComponentDamageForm;